<div class="p-5 max-w-[1600px] mx-auto">
  <!-- Header -->
  <div class="flex justify-between items-center mb-5">
    <h1 class="text-2xl font-medium">{{ 'reservations.list.title' | translate }}</h1>
    <button mat-flat-button (click)="navigateToCreate()">
      <mat-icon>add</mat-icon>
      {{ 'reservations.list.create' | translate }}
    </button>
  </div>

  <!-- Search + column picker -->
  <div class="flex gap-3 mb-4 items-center">
    <mat-form-field class="flex-1" subscriptSizing="dynamic">
      <mat-label>{{ 'reservations.list.search-placeholder' | translate }}</mat-label>
      <input #searchBox matInput [value]="searchInput()" (input)="onSearchInput(searchBox.value)" />
      <mat-icon matSuffix>search</mat-icon>
    </mat-form-field>

    <button
      mat-icon-button
      [disabled]="!hasActiveFilters()"
      (click)="clearAllFilters()"
      matTooltip="{{ 'reservations.filter.clear-all' | translate }}"
      [attr.aria-label]="'reservations.filter.clear-all' | translate">
      <mat-icon>filter_alt_off</mat-icon>
    </button>

    <button
      mat-icon-button
      [matMenuTriggerFor]="columnMenu"
      matTooltip="{{ 'reservations.list.column-picker' | translate }}"
      [attr.aria-label]="'reservations.list.column-picker' | translate">
      <mat-icon>view_column</mat-icon>
    </button>
  </div>

  <mat-card>
    <mat-card-content class="!p-0">
      <div class="overflow-x-auto">
        <table mat-table [dataSource]="reservations()" class="w-full">

          <ng-container matColumnDef="reservationNumber">
            <th mat-header-cell *matHeaderCellDef>{{ 'reservations.field.reservationNumber' | translate }}</th>
            <td mat-cell *matCellDef="let r" class="font-medium whitespace-nowrap">{{ r.reservationNumber }}</td>
          </ng-container>

          <ng-container matColumnDef="siteName">
            <th mat-header-cell *matHeaderCellDef>{{ 'reservations.field.site' | translate }}</th>
            <td mat-cell *matCellDef="let r">{{ codeListsService.siteMap().get(r.siteId) }}</td>
          </ng-container>

          <ng-container matColumnDef="customerName">
            <th mat-header-cell *matHeaderCellDef>{{ 'reservations.field.customer' | translate }}</th>
            <td mat-cell *matCellDef="let r">{{ r.customerName }}</td>
          </ng-container>

          <ng-container matColumnDef="waterTitle">
            <th mat-header-cell *matHeaderCellDef>{{ 'reservations.field.water' | translate }}</th>
            <td mat-cell *matCellDef="let r">{{ r.waterTitle }}</td>
          </ng-container>

          <ng-container matColumnDef="arrivalDate">
            <th mat-header-cell *matHeaderCellDef>{{ 'reservations.field.arrival' | translate }}</th>
            <td mat-cell *matCellDef="let r" class="whitespace-nowrap">{{ r.arrivalDate ? (r.arrivalDate | date: 'mediumDate') : '—' }}</td>
          </ng-container>

          <ng-container matColumnDef="departureDate">
            <th mat-header-cell *matHeaderCellDef>{{ 'reservations.field.departure' | translate }}</th>
            <td mat-cell *matCellDef="let r" class="whitespace-nowrap">{{ r.departureDate ? (r.departureDate | date: 'mediumDate') : '—' }}</td>
          </ng-container>

          <ng-container matColumnDef="statusName">
            <th mat-header-cell *matHeaderCellDef>{{ 'reservations.field.status' | translate }}</th>
            <td mat-cell *matCellDef="let r">
              <span class="px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                {{ codeListsService.reservationStatusMap().get(r.statusId) }}
              </span>
            </td>
          </ng-container>

          <ng-container matColumnDef="paymentStatus">
            <th mat-header-cell *matHeaderCellDef>{{ 'reservations.field.paymentStatus' | translate }}</th>
            <td mat-cell *matCellDef="let r">
              <span class="px-2 py-0.5 rounded text-xs font-medium" [class]="getPaymentStatusClass(r.paymentStatus)">
                {{ 'reservations.paymentStatus.' + r.paymentStatus | translate }}
              </span>
            </td>
          </ng-container>

          <ng-container matColumnDef="retailPrice">
            <th mat-header-cell *matHeaderCellDef>{{ 'reservations.field.retailPrice' | translate }}</th>
            <td mat-cell *matCellDef="let r" class="whitespace-nowrap">{{ r.retailPrice | currency: 'EUR' }}</td>
          </ng-container>

          <ng-container matColumnDef="reservationType">
            <th mat-header-cell *matHeaderCellDef>{{ 'reservations.field.type' | translate }}</th>
            <td mat-cell *matCellDef="let r">{{ r.reservationType ? ('reservations.type.' + r.reservationType | translate) : '' }}</td>
          </ng-container>

          <ng-container matColumnDef="companyName">
            <th mat-header-cell *matHeaderCellDef>{{ 'reservations.field.companyName' | translate }}</th>
            <td mat-cell *matCellDef="let r">{{ r.companyName }}</td>
          </ng-container>

          <ng-container matColumnDef="lastMinute">
            <th mat-header-cell *matHeaderCellDef>{{ 'reservations.field.lastMinute' | translate }}</th>
            <td mat-cell *matCellDef="let r"><app-bool [value]="r.lastMinute" /></td>
          </ng-container>

          <ng-container matColumnDef="insurance">
            <th mat-header-cell *matHeaderCellDef>{{ 'reservations.field.insurance' | translate }}</th>
            <td mat-cell *matCellDef="let r"><app-bool [value]="r.insurance" /></td>
          </ng-container>

          <ng-container matColumnDef="dog">
            <th mat-header-cell *matHeaderCellDef>{{ 'reservations.field.dog' | translate }}</th>
            <td mat-cell *matCellDef="let r">
              <app-bool [value]="r.dog" />
              @if (r.dog && r.dogBreed) { <span class="text-xs text-gray-500 ml-1">{{ r.dogBreed }}</span> }
            </td>
          </ng-container>

          <ng-container matColumnDef="financial">
            <th mat-header-cell *matHeaderCellDef>{{ 'reservations.field.financial' | translate }}</th>
            <td mat-cell *matCellDef="let r"><app-bool [value]="r.financial" /></td>
          </ng-container>

          <ng-container matColumnDef="financialNumber">
            <th mat-header-cell *matHeaderCellDef>{{ 'reservations.field.financialNumber' | translate }}</th>
            <td mat-cell *matCellDef="let r">{{ r.financialNumber }}</td>
          </ng-container>

          <ng-container matColumnDef="financialNumberBuy">
            <th mat-header-cell *matHeaderCellDef>{{ 'reservations.field.financialNumberBuy' | translate }}</th>
            <td mat-cell *matCellDef="let r">{{ r.financialNumberBuy }}</td>
          </ng-container>

          <ng-container matColumnDef="purchasePrice">
            <th mat-header-cell *matHeaderCellDef>{{ 'reservations.field.purchasePrice' | translate }}</th>
            <td mat-cell *matCellDef="let r" class="whitespace-nowrap">{{ r.purchasePrice | currency: 'EUR' }}</td>
          </ng-container>

          <ng-container matColumnDef="payedDownPayment">
            <th mat-header-cell *matHeaderCellDef>{{ 'reservations.field.payedDownPayment' | translate }}</th>
            <td mat-cell *matCellDef="let r"><app-bool [value]="r.payedDownPayment" /></td>
          </ng-container>

          <ng-container matColumnDef="downPaymentPrice">
            <th mat-header-cell *matHeaderCellDef>{{ 'reservations.field.downPaymentPrice' | translate }}</th>
            <td mat-cell *matCellDef="let r" class="whitespace-nowrap">{{ r.downPaymentPrice !== null ? (r.downPaymentPrice | currency: 'EUR') : '—' }}</td>
          </ng-container>

          <ng-container matColumnDef="payedRestPayment">
            <th mat-header-cell *matHeaderCellDef>{{ 'reservations.field.payedRestPayment' | translate }}</th>
            <td mat-cell *matCellDef="let r"><app-bool [value]="r.payedRestPayment" /></td>
          </ng-container>

          <ng-container matColumnDef="restPaymentPrice">
            <th mat-header-cell *matHeaderCellDef>{{ 'reservations.field.restPaymentPrice' | translate }}</th>
            <td mat-cell *matCellDef="let r" class="whitespace-nowrap">{{ r.restPaymentPrice !== null ? (r.restPaymentPrice | currency: 'EUR') : '—' }}</td>
          </ng-container>

          <ng-container matColumnDef="emergencyContact">
            <th mat-header-cell *matHeaderCellDef>{{ 'reservations.field.emergencyContact' | translate }}</th>
            <td mat-cell *matCellDef="let r">{{ r.emergencyContact }}</td>
          </ng-container>

          <ng-container matColumnDef="createdOn">
            <th mat-header-cell *matHeaderCellDef>{{ 'reservations.field.createdOn' | translate }}</th>
            <td mat-cell *matCellDef="let r" class="whitespace-nowrap">{{ r.createdOn | date: 'mediumDate' }}</td>
          </ng-container>

          <ng-container matColumnDef="invoiceDateOne">
            <th mat-header-cell *matHeaderCellDef>{{ 'reservations.field.invoiceOne' | translate }}</th>
            <td mat-cell *matCellDef="let r" class="whitespace-nowrap">{{ r.invoiceDateOne ? (r.invoiceDateOne | date: 'mediumDate') : '—' }}</td>
          </ng-container>

          <ng-container matColumnDef="invoiceDateTwo">
            <th mat-header-cell *matHeaderCellDef>{{ 'reservations.field.invoiceTwo' | translate }}</th>
            <td mat-cell *matCellDef="let r" class="whitespace-nowrap">{{ r.invoiceDateTwo ? (r.invoiceDateTwo | date: 'mediumDate') : '—' }}</td>
          </ng-container>

          <ng-container matColumnDef="cancelledOn">
            <th mat-header-cell *matHeaderCellDef>{{ 'reservations.field.cancelledOn' | translate }}</th>
            <td mat-cell *matCellDef="let r" class="whitespace-nowrap">{{ r.cancelledOn ? (r.cancelledOn | date: 'mediumDate') : '—' }}</td>
          </ng-container>

          <!-- Actions Column (no delete — reservations are cancelled, see CS-50) -->
          <ng-container matColumnDef="actions">
            <th mat-header-cell *matHeaderCellDef class="!w-16"></th>
            <td mat-cell *matCellDef="let r">
              <button
                mat-icon-button
                color="primary"
                (click)="$event.stopPropagation(); navigateToReservation(r.id)"
                matTooltip="{{ 'reservations.list.edit' | translate }}"
                [attr.aria-label]="'reservations.list.edit' | translate">
                <mat-icon>edit</mat-icon>
              </button>
            </td>
          </ng-container>

          <!-- Filter header columns (generated from column definitions) -->
          @for (col of allColumns; track col.key) {
            <ng-container [matColumnDef]="'filter-' + col.key">
              <th mat-header-cell *matHeaderCellDef [class.!p-1]="col.filterType !== 'none'">
                @switch (col.filterType) {
                  @case ('text') {
                    <mat-form-field subscriptSizing="dynamic" class="w-full">
                      <input #filterBox matInput
                        placeholder="{{ 'reservations.filter.placeholder' | translate }}"
                        [value]="getTextFilterValue(col.filterField!)"
                        (input)="onTextFilterInput(col.filterField!, filterBox.value)" />
                    </mat-form-field>
                  }
                  @case ('dropdown') {
                    <mat-form-field subscriptSizing="dynamic" class="w-full">
                      <mat-select
                        [value]="getDropdownFilterValue(col.filterField!)"
                        (selectionChange)="onDropdownFilterChange(col.filterField!, $event.value)">
                        <mat-option value="">{{ 'reservations.filter.all' | translate }}</mat-option>
                        @for (opt of dropdownSources[col.dropdownSource!](); track opt.id) {
                          <mat-option [value]="opt.id">{{ translatedSources[col.dropdownSource!] ? (opt.name | translate) : opt.name }}</mat-option>
                        }
                      </mat-select>
                    </mat-form-field>
                  }
                  @case ('boolean') {
                    <mat-form-field subscriptSizing="dynamic" class="w-full">
                      <mat-select
                        [value]="getBooleanFilterValue(col.filterField!)"
                        (selectionChange)="onDropdownFilterChange(col.filterField!, $event.value)">
                        <mat-option value="">{{ 'reservations.filter.all' | translate }}</mat-option>
                        <mat-option [value]="true">{{ 'reservations.filter.yes' | translate }}</mat-option>
                        <mat-option [value]="false">{{ 'reservations.filter.no' | translate }}</mat-option>
                      </mat-select>
                    </mat-form-field>
                  }
                  @case ('daterange') {
                    <div class="flex gap-1 items-center">
                      <mat-form-field subscriptSizing="dynamic" class="flex-1">
                        <mat-label>{{ 'reservations.filter.from' | translate }}</mat-label>
                        <input matInput [matDatepicker]="fromPicker"
                          [value]="getDateFilterValue(col.filterFieldFrom!)"
                          (dateChange)="onDateFilterChange(col.filterFieldFrom!, $event.value)" />
                        @if (getDateFilterValue(col.filterFieldFrom!)) {
                          <button matIconSuffix mat-icon-button type="button"
                            (click)="onDateFilterChange(col.filterFieldFrom!, null)"
                            matTooltip="{{ 'reservations.filter.clear' | translate }}"
                            [attr.aria-label]="'reservations.filter.clear' | translate">
                            <mat-icon>close</mat-icon>
                          </button>
                        } @else {
                          <mat-datepicker-toggle matIconSuffix [for]="fromPicker" />
                        }
                        <mat-datepicker #fromPicker />
                      </mat-form-field>
                      <span class="text-gray-400 text-xs">→</span>
                      <mat-form-field subscriptSizing="dynamic" class="flex-1">
                        <mat-label>{{ 'reservations.filter.to' | translate }}</mat-label>
                        <input matInput [matDatepicker]="toPicker"
                          [value]="getDateFilterValue(col.filterFieldTo!)"
                          (dateChange)="onDateFilterChange(col.filterFieldTo!, $event.value)" />
                        @if (getDateFilterValue(col.filterFieldTo!)) {
                          <button matIconSuffix mat-icon-button type="button"
                            (click)="onDateFilterChange(col.filterFieldTo!, null)"
                            matTooltip="{{ 'reservations.filter.clear' | translate }}"
                            [attr.aria-label]="'reservations.filter.clear' | translate">
                            <mat-icon>close</mat-icon>
                          </button>
                        } @else {
                          <mat-datepicker-toggle matIconSuffix [for]="toPicker" />
                        }
                        <mat-datepicker #toPicker />
                      </mat-form-field>
                    </div>
                  }
                  @case ('numrange') {
                    <div class="flex gap-1">
                      <mat-form-field subscriptSizing="dynamic" class="flex-1">
                        <input matInput type="number"
                          placeholder="{{ 'reservations.filter.min' | translate }}"
                          [value]="getRangeFilterValue(col.filterFieldFrom!)"
                          (change)="onRangeFilterChange(col.filterFieldFrom!, $any($event.target).value, true)" />
                      </mat-form-field>
                      <mat-form-field subscriptSizing="dynamic" class="flex-1">
                        <input matInput type="number"
                          placeholder="{{ 'reservations.filter.max' | translate }}"
                          [value]="getRangeFilterValue(col.filterFieldTo!)"
                          (change)="onRangeFilterChange(col.filterFieldTo!, $any($event.target).value, true)" />
                      </mat-form-field>
                    </div>
                  }
                }
              </th>
            </ng-container>
          }

          <ng-container matColumnDef="filter-actions">
            <th mat-header-cell *matHeaderCellDef class="!w-16"></th>
          </ng-container>

          <!-- No-data row -->
          <tr *matNoDataRow>
            @if (!reservationsResource.isLoading()) {
              <td [attr.colspan]="displayedColumns().length" class="p-0">
                <div class="text-center p-10">
                  <mat-icon class="!text-6xl !w-16 !h-16 !text-gray-400 mb-4">event_busy</mat-icon>
                  <p class="text-gray-700 text-base">{{ 'reservations.list.no-reservations' | translate }}</p>
                </div>
              </td>
            }
          </tr>

          <!-- Row definitions -->
          <tr mat-header-row *matHeaderRowDef="displayedColumns()"></tr>
          <tr mat-header-row *matHeaderRowDef="filterColumns()" class="filter-row"></tr>
          <tr
            mat-row
            *matRowDef="let row; columns: displayedColumns()"
            (click)="navigateToReservation(row.id)"
            class="cursor-pointer hover:bg-gray-50"></tr>
        </table>
      </div>

      @if (totalCount() > 0) {
        <mat-paginator
          [length]="totalCount()"
          [pageSize]="pageSize()"
          [pageIndex]="page() - 1"
          [pageSizeOptions]="[10, 25, 50, 100]"
          [showFirstLastButtons]="true"
          (page)="onPageChange($event)"
          class="border-t border-gray-200" />
      }
    </mat-card-content>
  </mat-card>
</div>

<!-- Column picker menu -->
<mat-menu #columnMenu="matMenu">
  <div class="px-2 py-1 min-w-48" (click)="$event.stopPropagation()">
    <p class="text-xs font-medium text-gray-500 uppercase tracking-wider px-2 pb-2">
      {{ 'reservations.list.column-picker' | translate }}
    </p>
    @for (col of allColumns; track col.key) {
      <div class="flex items-center px-2 py-1">
        <mat-checkbox [checked]="isColumnVisible(col.key)" (change)="toggleColumn(col.key)">
          {{ col.labelKey | translate }}
        </mat-checkbox>
      </div>
    }
  </div>
</mat-menu>
